/**
 * Shared filter constants, types, and utilities.
 *
 * Centralizes filter-related definitions that were previously duplicated
 * across every section's constants file.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

/** Metadata for a filter field — determines the input type rendered by FilterBuilder. */
export type FilterFieldMeta = Record<
  string,
  {
    type: 'text' | 'dropdown';
    placeholder?: string;
    options?: Array<{ value: string; label: string }>;
  }
>;

// ─── Shared Operators ───────────────────────────────────────────────────────

/**
 * Full operator set used by most sections (Purchases, Users, Push, Product).
 * Marketing uses a subset (equality + contains only).
 */
export const COMMON_FILTER_OPERATORS = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' },
  { value: '>=', label: 'greater or equal' },
  { value: '<=', label: 'less or equal' },
  { value: 'CONTAINS', label: 'contains' },
  { value: 'NOT_CONTAINS', label: 'not contains' },
] as const;

/** Reduced operator set for Marketing (no numeric comparisons). */
export const EQUALITY_FILTER_OPERATORS = [
  { value: '=', label: 'equals' },
  { value: '!=', label: 'not equals' },
  { value: 'CONTAINS', label: 'contains' },
  { value: 'NOT_CONTAINS', label: 'not contains' },
] as const;

// ─── Shared Dropdown Options ────────────────────────────────────────────────

export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
] as const;

export const PLATFORM_OPTIONS_IOS_ANDROID = [
  { value: 'IOS', label: 'iOS' },
  { value: 'ANDROID', label: 'Android' },
] as const;

export const PLATFORM_OPTIONS_WITH_WEB = [
  { value: 'iOS', label: 'iOS' },
  { value: 'Android', label: 'Android' },
  { value: 'Web', label: 'Web' },
] as const;

// ─── Filter Utilities ───────────────────────────────────────────────────────

/**
 * Decode a base64-encoded filter JSON string.
 * Supports both grouped format `{ groups: [...] }` and legacy flat array.
 * Returns the parsed object, or null on failure.
 */
export function decodeBase64Filters(encoded: string): unknown {
  if (!encoded) return null;
  try {
    return JSON.parse(atob(encoded));
  } catch {
    return null;
  }
}

/**
 * Count the total number of filter conditions from a raw filters value.
 * Handles base64-encoded strings (grouped or flat) and plain arrays.
 */
export function countFilters(filtersRaw: unknown): number {
  if (typeof filtersRaw === 'string' && filtersRaw) {
    try {
      const parsed = JSON.parse(atob(filtersRaw));
      // New grouped format
      if (parsed && Array.isArray(parsed.groups)) {
        return parsed.groups.reduce(
          (sum: number, g: { conditions?: unknown[] }) =>
            sum + (g.conditions?.length || 0),
          0,
        );
      }
      // Legacy flat array
      if (Array.isArray(parsed)) return parsed.length;
    } catch {
      /* ignore */
    }
  }
  if (Array.isArray(filtersRaw)) return filtersRaw.length;
  return 0;
}
